import { AlertTriangle, Gift, Mail, Phone, UserPlus } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { CustomerAnalyticsQuery } from "@/gql/graphql";

interface CustomerRiskAlertsProps {
  analytics: NonNullable<CustomerAnalyticsQuery["customerAnalytics"]>;
  onAction?: (action: string, customerId: string) => void;
  loading?: boolean;
}

export function CustomerRiskAlerts({
  analytics,
  onAction,
  loading = false,
}: CustomerRiskAlertsProps) {
  if (loading) {
    return <RiskAlertsSkeleton />;
  }

  const { churnRisk, visitFrequency } = analytics;

  // Only show for medium+ risk or new customers with specific conditions
  const shouldShowAlert =
    ["CRITICAL", "HIGH", "MEDIUM"].includes(churnRisk.churnRiskLevel) ||
    (churnRisk.churnRiskLevel === "NEW_CUSTOMER" && visitFrequency.totalVisits <= 2);

  if (!shouldShowAlert) {
    return null;
  }

  const getRiskConfig = (level: string) => {
    switch (level) {
      case "CRITICAL":
        return {
          variant: "destructive" as const,
          icon: <AlertTriangle className="w-5 h-5" />,
          title: "Critical Churn Risk",
          urgency: "URGENT",
          colorClass: "border-red-500 bg-red-50 dark:bg-red-950/50",
        };
      case "HIGH":
        return {
          variant: "destructive" as const,
          icon: <AlertTriangle className="w-5 h-5" />,
          title: "High Churn Risk",
          urgency: "HIGH",
          colorClass: "border-orange-500 bg-orange-50 dark:bg-orange-950/50",
        };
      case "MEDIUM":
        return {
          variant: "default" as const,
          icon: <AlertTriangle className="w-5 h-5" />,
          title: "Medium Churn Risk",
          urgency: "MEDIUM",
          colorClass: "border-yellow-500 bg-yellow-50 dark:bg-yellow-950/50",
        };
      case "NEW_CUSTOMER":
        return {
          variant: "default" as const,
          icon: <UserPlus className="w-5 h-5" />,
          title: "New Customer Opportunity",
          urgency: "NURTURE",
          colorClass: "border-blue-500 bg-blue-50 dark:bg-blue-950/50",
        };
      default:
        return {
          variant: "default" as const,
          icon: <AlertTriangle className="w-5 h-5" />,
          title: "Attention Required",
          urgency: "INFO",
          colorClass: "border-gray-500 bg-gray-50 dark:bg-gray-950/50",
        };
    }
  };

  const getActionButtons = (level: string) => {
    const baseActions = [
      {
        icon: <Phone className="w-4 h-4" />,
        label: "Call Customer",
        action: "call",
        variant: "default" as const,
      },
      {
        icon: <Mail className="w-4 h-4" />,
        label: "Send Email",
        action: "email",
        variant: "outline" as const,
      },
    ];

    switch (level) {
      case "CRITICAL":
      case "HIGH":
        return [
          ...baseActions,
          {
            icon: <Gift className="w-4 h-4" />,
            label: "Send Offer",
            action: "offer",
            variant: "secondary" as const,
          },
        ];
      case "MEDIUM":
        return [
          ...baseActions,
          {
            icon: <Gift className="w-4 h-4" />,
            label: "Send Promotion",
            action: "promotion",
            variant: "secondary" as const,
          },
        ];
      case "NEW_CUSTOMER":
        return [
          {
            icon: <Mail className="w-4 h-4" />,
            label: "Welcome Email",
            action: "welcome",
            variant: "default" as const,
          },
          {
            icon: <Phone className="w-4 h-4" />,
            label: "Follow Up",
            action: "followup",
            variant: "outline" as const,
          },
        ];
      default:
        return baseActions;
    }
  };

  const config = getRiskConfig(churnRisk.churnRiskLevel);
  const actions = getActionButtons(churnRisk.churnRiskLevel);

  const formatDaysOverdue = (days: number) => {
    if (days <= 0) return "";
    if (days === 1) return "1 day overdue";
    if (days < 7) return `${days} days overdue`;
    if (days < 30)
      return `${Math.round(days / 7)} week${Math.round(days / 7) !== 1 ? "s" : ""} overdue`;
    return `${Math.round(days / 30)} month${Math.round(days / 30) !== 1 ? "s" : ""} overdue`;
  };

  return (
    <Card className={config.colorClass}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {config.icon}
            <span>{config.title}</span>
          </div>
          <div
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              config.urgency === "URGENT"
                ? "bg-red-100 text-red-900 dark:bg-red-900 dark:text-red-100"
                : config.urgency === "HIGH"
                  ? "bg-orange-100 text-orange-900 dark:bg-orange-900 dark:text-orange-100"
                  : config.urgency === "MEDIUM"
                    ? "bg-yellow-100 text-yellow-900 dark:bg-yellow-900 dark:text-yellow-100"
                    : config.urgency === "NURTURE"
                      ? "bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100"
                      : "bg-gray-100 text-gray-900 dark:bg-gray-900 dark:text-gray-100"
            }`}
          >
            {config.urgency}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Risk Details */}
        <Alert variant={config.variant}>
          <AlertDescription className="space-y-2">
            <div className="font-medium">
              {churnRisk.churnRiskLevel === "NEW_CUSTOMER" ? (
                <>New customer needs nurturing to build loyalty</>
              ) : (
                <>
                  Customer hasn't visited in {churnRisk.daysSinceLastVisit} days
                  {churnRisk.overdueDays > 0 && (
                    <span className="ml-1">({formatDaysOverdue(churnRisk.overdueDays)})</span>
                  )}
                </>
              )}
            </div>

            {/* Recommended Action */}
            <div className="text-sm">
              <strong>Recommended Action:</strong> {churnRisk.recommendedAction}
            </div>

            {/* Risk Metrics */}
            <div className="grid grid-cols-2 gap-4 text-sm pt-2 border-t">
              <div>
                <span className="text-muted-foreground">Risk Score:</span>
                <span className="ml-2 font-medium">{churnRisk.totalRiskScore}/12</span>
              </div>
              <div>
                <span className="text-muted-foreground">Total Visits:</span>
                <span className="ml-2 font-medium">{visitFrequency.totalVisits}</span>
              </div>
            </div>
          </AlertDescription>
        </Alert>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          {actions.map((action) => (
            <Button
              key={action.action}
              variant={action.variant}
              size="sm"
              onClick={() => onAction?.(action.action, analytics.customerId)}
              className="flex items-center gap-2"
            >
              {action.icon}
              {action.label}
            </Button>
          ))}
        </div>

        {/* Next Steps for Critical/High Risk */}
        {["CRITICAL", "HIGH"].includes(churnRisk.churnRiskLevel) && (
          <div className="bg-white dark:bg-gray-900 p-3 rounded border border-current/20">
            <div className="font-medium text-sm mb-2">Immediate Next Steps:</div>
            <ul className="text-sm space-y-1 list-disc list-inside text-muted-foreground">
              {churnRisk.churnRiskLevel === "CRITICAL" && (
                <>
                  <li>Personal phone call within 24 hours</li>
                  <li>Offer incentive or loyalty bonus</li>
                  <li>Schedule preferred appointment time</li>
                </>
              )}
              {churnRisk.churnRiskLevel === "HIGH" && (
                <>
                  <li>Personal outreach within 3 days</li>
                  <li>Send targeted promotion</li>
                  <li>Remind of upcoming services needed</li>
                </>
              )}
            </ul>
          </div>
        )}

        {/* New Customer Nurturing */}
        {churnRisk.churnRiskLevel === "NEW_CUSTOMER" && visitFrequency.totalVisits <= 2 && (
          <div className="bg-white dark:bg-gray-900 p-3 rounded border border-current/20">
            <div className="font-medium text-sm mb-2">New Customer Strategy:</div>
            <ul className="text-sm space-y-1 list-disc list-inside text-muted-foreground">
              <li>Send welcome series email campaign</li>
              <li>Schedule follow-up consultation call</li>
              <li>Introduce to preferred services</li>
              <li>Explain loyalty program benefits</li>
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RiskAlertsSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-48" />
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-16" />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2" />
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-3/4 mb-3" />
          <div className="grid grid-cols-2 gap-4">
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          </div>
        </div>
        <div className="flex gap-2">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-24" />
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-24" />
        </div>
      </CardContent>
    </Card>
  );
}
